import { motion, AnimatePresence } from "motion/react";
import { css } from "@emotion/react";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import React, { useCallback, useEffect, useState } from "react";
import { Button } from "../Button";
import { Overlay, OverlayProps } from "../Overlay";
import { hexToRgba } from "../@utils/hexToRgba";
import { useKeyboard } from "../hooks";
import { useTheme } from "../theme";

const MAX_IMAGE_WIDTH = 800;

interface ButtonWrapperProps extends React.ComponentPropsWithoutRef<"div"> {
  children?: React.ReactNode;
}

const ButtonWrapper = (props: ButtonWrapperProps) => {
  const theme = useTheme();

  const BUTTON_CONTAINER_COLOR = hexToRgba(
    theme.palette.background.overlay,
    0.2,
  );
  return (
    <div
      css={css`
        position: absolute;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        @media (max-width: ${MAX_IMAGE_WIDTH}px) {
          background-color: ${BUTTON_CONTAINER_COLOR};
        }
      `}
      {...props}
    />
  );
};

export interface ImageViewerProps extends OverlayProps {
  images: (React.ImgHTMLAttributes<HTMLImageElement> & { src: string })[];
  currentIndex: number;
}

export const ImageViewer = ({
  images,
  currentIndex,
  onClose,
  ...props
}: ImageViewerProps) => {
  useEffect(() => {
    /**
     * Preload images
     */
    images.forEach((image) => {
      const img = document.createElement("img");
      img.src = image.src;
    });
  }, [images]);

  const [index, setIndex] = useState(currentIndex);
  const [direction, setDirection] = useState<"next" | "prev">("next");
  const [prevCurrentIndex, setPrevCurrentIndex] = useState(currentIndex);

  if (prevCurrentIndex !== currentIndex) {
    setPrevCurrentIndex(currentIndex);
    setIndex(currentIndex);
  }

  const handlePrev = useCallback(() => {
    setDirection("prev");
    setIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const handleNext = useCallback(() => {
    setDirection("next");
    setIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  useKeyboard("Escape", onClose);
  useKeyboard("ArrowRight", handleNext);
  useKeyboard("ArrowLeft", handlePrev);

  return (
    <Overlay fullScreen {...props} onClose={onClose}>
      <div
        css={css`
          position: relative;
          top: 0;
          bottom: 0;
          box-sizing: border-box;
          display: flex;
          align-items: stretch;
          justify-content: center;
          max-height: calc(100% - 32px);
          margin: auto -14px;
        `}
      >
        <div
          css={css`
            position: relative;
            box-sizing: border-box;
            max-width: ${MAX_IMAGE_WIDTH}px;
            overflow: hidden;
            will-change: opacity, transform;
          `}
        >
          <AnimatePresence initial={false}>
            <motion.div
              key={index}
              initial={{
                x: direction === "next" ? "100%" : "-100%",
              }}
              animate={{
                x: 0,
                position: "relative",
                width: "100%",
              }}
              exit={{
                x: direction === "next" ? "-100%" : "100%",
                position: "absolute",
                width: "100%",
                top: 0,
              }}
              transition={{
                ease: "easeInOut",
                duration: 0.3,
              }}
            >
              <img {...images[index]} alt="" />
            </motion.div>
          </AnimatePresence>
        </div>
        <ButtonWrapper
          css={css`
            left: 0px;
          `}
        >
          <Button
            icon={
              <NavigateBeforeIcon
                htmlColor="white"
                onClick={handlePrev}
                fontSize="large"
              />
            }
            size="large"
          />
        </ButtonWrapper>
        <ButtonWrapper
          css={css`
            right: 0px;
          `}
        >
          <Button
            icon={
              <NavigateNextIcon
                htmlColor="white"
                onClick={handleNext}
                fontSize="large"
              />
            }
            size="large"
          />
        </ButtonWrapper>
      </div>
    </Overlay>
  );
};
